import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Monitor, Moon, Sun } from "lucide-react";
import { AVAILABLE_THEMES, useVisualTheme } from "../lib/themeLoader";
import { getThemeDef, themePalette } from "../lib/themes";
import { useTheme, useThemeMode, type ThemeMode } from "../hooks/useTheme";
import { ThemeMiniature } from "./ThemeMiniature";

/** The mode choices, in the order the Waki Mac apps show them. */
const MODES: { id: ThemeMode; label: string; hint: string; Icon: typeof Sun }[] = [
  { id: "system", label: "System", hint: "Follows your system", Icon: Monitor },
  { id: "light", label: "Light", hint: "Always light", Icon: Sun },
  { id: "dark", label: "Dark", hint: "Always dark", Icon: Moon },
];

/** The Appearance panel: a System / Light / Dark switch, then one card per
 *  theme with a miniature of the app in that theme. Mirrors the appearance
 *  popover in the Waki Mac apps (WakiAppearancePopover).
 *
 *  Owns the live wiring; the wrapping surface (the sidebar popover, or any
 *  static panel) decides chrome and positioning. Choices apply immediately.
 *
 *  `scrollGrid` caps the panel's height so it scrolls inside a popover (the
 *  cap fits all five themes on an ordinary window; 2026-10-02). Pass
 *  false when the panel sits inside something that already scrolls (a sheet),
 *  where a nested scroll box makes a swipe do different things depending on
 *  where the thumb lands. */
export function ThemePickerPanel({ scrollGrid = true }: { scrollGrid?: boolean } = {}) {
  const { theme: active, setTheme } = useVisualTheme();
  const { mode, setMode } = useThemeMode();
  // Previews follow the mode actually showing, so "System" previews whatever
  // the system resolved to.
  const { theme: resolved } = useTheme();
  const dark = resolved === "dark";

  return (
    <div className={`flex flex-col gap-3.5 p-4 ${scrollGrid ? "max-h-[min(660px,85vh)] overflow-y-auto" : ""}`}>
      <div>
        <div className="text-[15px] font-semibold leading-tight text-slate-900 dark:text-slate-100">Appearance</div>
        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Pick a look. Mode decides light or dark.</div>
      </div>

      <div
        role="radiogroup"
        aria-label="Mode"
        className="flex gap-1 rounded-[10px] border border-slate-200 bg-slate-100 p-1 dark:border-slate-700 dark:bg-slate-950"
      >
        {MODES.map(({ id, label, hint, Icon }) => {
          const on = mode === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={on}
              title={hint}
              onClick={() => setMode(id)}
              className={`flex min-h-[28px] flex-1 items-center justify-center gap-1.5 rounded-[7px] text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                on ? "shadow-sm" : "text-slate-700 hover:bg-slate-200/70 dark:text-slate-200 dark:hover:bg-slate-800"
              }`}
              style={on ? { background: "var(--wov-accent)", color: "var(--wov-accent-contrast)" } : undefined}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
              {label}
            </button>
          );
        })}
      </div>

      <div role="radiogroup" aria-label="Theme" className="flex flex-col gap-2">
        {AVAILABLE_THEMES.map((t) => {
          const on = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setTheme(t.id)}
              className={`flex w-full items-center gap-3 rounded-[11px] border p-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                on
                  ? ""
                  : "border-slate-200 bg-transparent hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40"
              }`}
              style={
                on
                  ? {
                      background: "color-mix(in srgb, var(--wov-accent) 12%, transparent)",
                      borderColor: "color-mix(in srgb, var(--wov-accent) 80%, transparent)",
                    }
                  : undefined
              }
            >
              <ThemeMiniature palette={themePalette(getThemeDef(t.id), dark)} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-900 dark:text-slate-100">
                  {t.name}
                  {on && (
                    <CheckCircle2
                      aria-hidden="true"
                      className="h-3.5 w-3.5 flex-shrink-0"
                      strokeWidth={2.5}
                      style={{ color: "var(--wov-accent)" }}
                    />
                  )}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-slate-500 dark:text-slate-400">
                  {t.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface DropdownProps {
  open: boolean;
  onClose: () => void;
  /** Where the popover sits relative to the trigger. "right" anchors
   *  to the right of the trigger (sidebar bottom-section pattern);
   *  "top" stacks above (mobile or other compact callers). */
  anchor?: "right" | "top";
  /** Element to ignore for click-outside (typically the trigger button)
   *  so a click on the trigger toggles open/close cleanly without the
   *  outside-click handler immediately re-closing it. */
  ignoreRef?: React.RefObject<HTMLElement | null>;
}

/** Anchored popover wrapper around `ThemePickerPanel`. Closes on Esc
 *  and on click outside its own surface (and the trigger element, if
 *  one is passed via `ignoreRef`). A choice applies immediately and
 *  leaves the popover open so the user can compare.
 *
 *  It is rendered into <body> at a fixed position measured from the trigger
 *  when it opens. Positioned inside the sidebar, it moved whenever the rail
 *  widened on hover, which happens as the pointer travels to it: the popover
 *  slid away from under the pointer and the click landed outside, closing
 *  it without applying the theme. */
export function ThemePickerDropdown({ open, onClose, anchor = "right", ignoreRef }: DropdownProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties | null>(null);

  // Measure the trigger once per opening; the popover then stays put.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const r = ignoreRef?.current?.getBoundingClientRect();
    if (!r) {
      setPos({ left: 8, bottom: 8 });
      return;
    }
    setPos(
      anchor === "top"
        ? { left: Math.max(8, r.left), bottom: window.innerHeight - r.top + 8 }
        : { left: r.right + 8, bottom: Math.max(8, window.innerHeight - r.bottom) },
    );
  }, [open, anchor, ignoreRef]);

  // Closes on a press anywhere else, Esc, or focus leaving for a frame (a
  // click in a message body lands in its iframe, which the page never
  // hears). The press is caught in the capture phase, so a pane that stops
  // its own clicks cannot keep the popover open (2026-10-02).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onPress = (e: PointerEvent) => {
      const node = ref.current;
      const ignore = ignoreRef?.current;
      const target = e.target as Node;
      if (node && !node.contains(target) && (!ignore || !ignore.contains(target))) closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    const onBlur = () => closeRef.current();
    // Defer one tick so the press that opened the popover doesn't
    // immediately register as an outside press.
    const t = setTimeout(() => document.addEventListener("pointerdown", onPress, true), 0);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", onPress, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
    };
  }, [open, ignoreRef]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Appearance"
      style={pos}
      className="fixed z-50 w-[320px] max-w-[calc(100vw-1rem)] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl shadow-slate-300/40 dark:shadow-black/50 overflow-hidden animate-popoverIn motion-reduce:animate-none"
    >
      <ThemePickerPanel />
    </div>,
    document.body,
  );
}
